import { useState, useEffect, type JSX } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import { InputText } from 'primereact/inputtext'
import { InputSwitch } from 'primereact/inputswitch'
import { Dropdown } from 'primereact/dropdown'
import { useSmsGateways, type SmsGatewayProvider } from '../../../hooks/useSmsGateways.js'
import { useCampaigns } from '../../../hooks/useCampaigns.js'
import {
  FormContainer,
  FormGrid,
  FormField,
  FormDivider,
  StepperSection,
  StickyFooter,
  type StepItem,
} from '../../../components/forms/layout/index.js'

const providerOptions = [
  { label: 'PLDT', value: 'pldt' },
  { label: 'Mobile 360', value: 'mobile360' },
  { label: 'Twilio', value: 'twilio' },
  { label: 'Yeastar', value: 'yeastar' },
]

export default function SmsGatewayFormPage(): JSX.Element {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { id } = useParams<{ id: string }>()
  const isViewMode = searchParams.get('view') === 'true'
  const isEditMode = id && id !== 'new' && !isViewMode

  const { create, update, getById } = useSmsGateways()
  const { campaigns, loading: campaignsLoading } = useCampaigns()
  const [loading, setLoading] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)

  const [formData, setFormData] = useState({
    status: true,
    isDefault: false,
    name: '',
    description: '',
    campaignId: '',
    provider: 'pldt' as SmsGatewayProvider,
    providerConfig: {
      // PLDT
      username: '',
      password: '',
      // Mobile 360
      mobile360Username: '',
      mobile360Password: '',
      shortcodeMask: '',
      // Twilio
      sid: '',
      authToken: '',
      fromNumber: '',
      statusUrl: '',
      replyUrl: '',
      // Yeastar
      ip: '',
      port: '',
      yeastarUsername: '',
      yeastarPassword: '',
    },
  })

  const [errors, setErrors] = useState({
    name: '',
    provider: '',
  })

  useEffect(() => {
    if (id && id !== 'new') {
      setLoading(true)
      getById(id)
        .then((data) => {
          const config = data.item.providerConfig as Record<string, string>
          setFormData({
            status: data.item.status ?? true,
            isDefault: data.item.isDefault ?? false,
            name: data.item.name ?? '',
            description: data.item.description ?? '',
            campaignId: data.item.campaignId ?? '',
            provider: data.item.provider ?? 'pldt',
            providerConfig: {
              username: config.username || '',
              password: config.password || '',
              mobile360Username: config.mobile360Username || '',
              mobile360Password: config.mobile360Password || '',
              shortcodeMask: config.shortcodeMask || '',
              sid: config.sid || '',
              authToken: config.authToken || '',
              fromNumber: config.fromNumber || '',
              statusUrl: config.statusUrl || '',
              replyUrl: config.replyUrl || '',
              ip: config.ip || '',
              port: config.port || '',
              yeastarUsername: config.yeastarUsername || '',
              yeastarPassword: config.yeastarPassword || '',
            },
          })
        })
        .catch((error) => {
          console.error('Error fetching SMS gateway:', error)
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }, [id, getById])

  const validateForm = (): boolean => {
    const newErrors = {
      name: '',
      provider: '',
    }

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required'
    }

    setErrors(newErrors)
    if (newErrors.name) setActiveIndex(0)
    return !newErrors.name && !newErrors.provider
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setLoading(true)
    try {
      // Build provider config based on selected provider
      const providerConfig: Record<string, string> = {}

      if (formData.provider === 'pldt') {
        providerConfig.username = formData.providerConfig.username
        providerConfig.password = formData.providerConfig.password
      } else if (formData.provider === 'mobile360') {
        providerConfig.username = formData.providerConfig.mobile360Username
        providerConfig.password = formData.providerConfig.mobile360Password
        providerConfig.shortcodeMask = formData.providerConfig.shortcodeMask
      } else if (formData.provider === 'twilio') {
        providerConfig.sid = formData.providerConfig.sid
        providerConfig.authToken = formData.providerConfig.authToken
        providerConfig.fromNumber = formData.providerConfig.fromNumber
        providerConfig.statusUrl = formData.providerConfig.statusUrl
        providerConfig.replyUrl = formData.providerConfig.replyUrl
      } else if (formData.provider === 'yeastar') {
        providerConfig.ip = formData.providerConfig.ip
        providerConfig.port = formData.providerConfig.port
        providerConfig.username = formData.providerConfig.yeastarUsername
        providerConfig.password = formData.providerConfig.yeastarPassword
      }

      const payload = {
        status: formData.status,
        isDefault: formData.isDefault,
        name: formData.name,
        description: formData.description || null,
        campaignId: formData.campaignId || null,
        provider: formData.provider,
        providerConfig,
      }

      if (isEditMode) {
        await update(id!, payload)
      } else {
        await create(payload)
      }

      navigate('/admin/channels/sms-gateways')
    } catch (error) {
      console.error('Error saving SMS gateway:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    navigate('/admin/channels/sms-gateways')
  }

  const title = isViewMode ? 'View SMS Gateway' : isEditMode ? 'Edit SMS Gateway' : 'Add SMS Gateway'
  const description = isViewMode
    ? 'View SMS gateway configuration details.'
    : isEditMode
      ? 'Edit SMS gateway configuration.'
      : 'Configure a new SMS gateway provider.'

  if (loading && id && id !== 'new') {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <i className="pi pi-spinner pi-spin text-3xl text-[var(--kc3-accent)]" />
          <span className="text-sm font-medium text-gray-600">Loading SMS gateway data...</span>
        </div>
      </div>
    )
  }

  const setProviderConfig = (field: string, value: string): void => {
    setFormData((prev) => ({ ...prev, providerConfig: { ...prev.providerConfig, [field]: value } }))
  }

  const bodyClass = `space-y-5 p-6 ${isViewMode ? 'pointer-events-none' : ''}`

  const renderProviderFields = (): JSX.Element => {
    switch (formData.provider) {
      case 'pldt':
        return (
          <FormGrid columns={2}>
            <FormField label="PLDT Username" htmlFor="pldtUsername">
              <InputText
                id="pldtUsername"
                value={formData.providerConfig.username}
                onChange={(e) => setProviderConfig('username', e.target.value)}
                disabled={isViewMode}
                className="w-full"
                placeholder="Enter PLDT username"
              />
            </FormField>
            <FormField label="PLDT Password" htmlFor="pldtPassword">
              <InputText
                id="pldtPassword"
                value={formData.providerConfig.password}
                onChange={(e) => setProviderConfig('password', e.target.value)}
                disabled={isViewMode}
                className="w-full"
                placeholder="Enter PLDT password"
                type="password"
              />
            </FormField>
          </FormGrid>
        )
      case 'mobile360':
        return (
          <FormGrid columns={2}>
            <FormField label="Mobile 360 Username" htmlFor="m360Username">
              <InputText
                id="m360Username"
                value={formData.providerConfig.mobile360Username}
                onChange={(e) => setProviderConfig('mobile360Username', e.target.value)}
                disabled={isViewMode}
                className="w-full"
                placeholder="Enter Mobile 360 username"
              />
            </FormField>
            <FormField label="Mobile 360 Password" htmlFor="m360Password">
              <InputText
                id="m360Password"
                value={formData.providerConfig.mobile360Password}
                onChange={(e) => setProviderConfig('mobile360Password', e.target.value)}
                disabled={isViewMode}
                className="w-full"
                placeholder="Enter Mobile 360 password"
                type="password"
              />
            </FormField>
            <FormField label="Shortcode Mask" htmlFor="shortcodeMask">
              <InputText
                id="shortcodeMask"
                value={formData.providerConfig.shortcodeMask}
                onChange={(e) => setProviderConfig('shortcodeMask', e.target.value)}
                disabled={isViewMode}
                className="w-full"
                placeholder="Enter shortcode mask"
              />
            </FormField>
          </FormGrid>
        )
      case 'twilio':
        return (
          <FormGrid columns={2}>
            <FormField label="Twilio SID" htmlFor="twilioSid">
              <InputText
                id="twilioSid"
                value={formData.providerConfig.sid}
                onChange={(e) => setProviderConfig('sid', e.target.value)}
                disabled={isViewMode}
                className="w-full"
                placeholder="Enter Twilio SID"
              />
            </FormField>
            <FormField label="Twilio Auth Token" htmlFor="twilioAuthToken">
              <InputText
                id="twilioAuthToken"
                value={formData.providerConfig.authToken}
                onChange={(e) => setProviderConfig('authToken', e.target.value)}
                disabled={isViewMode}
                className="w-full"
                placeholder="Enter Twilio auth token"
                type="password"
              />
            </FormField>
            <FormField label="Twilio From Number" htmlFor="twilioFromNumber">
              <InputText
                id="twilioFromNumber"
                value={formData.providerConfig.fromNumber}
                onChange={(e) => setProviderConfig('fromNumber', e.target.value)}
                disabled={isViewMode}
                className="w-full"
                placeholder="Enter Twilio from number"
              />
            </FormField>
            <FormField label="Twilio Status URL" htmlFor="twilioStatusUrl">
              <InputText
                id="twilioStatusUrl"
                value={formData.providerConfig.statusUrl}
                onChange={(e) => setProviderConfig('statusUrl', e.target.value)}
                disabled={isViewMode}
                className="w-full"
                placeholder="Enter Twilio status URL"
              />
            </FormField>
            <FormField label="Twilio Reply URL" htmlFor="twilioReplyUrl">
              <InputText
                id="twilioReplyUrl"
                value={formData.providerConfig.replyUrl}
                onChange={(e) => setProviderConfig('replyUrl', e.target.value)}
                disabled={isViewMode}
                className="w-full"
                placeholder="Enter Twilio reply URL"
              />
            </FormField>
          </FormGrid>
        )
      case 'yeastar':
        return (
          <FormGrid columns={2}>
            <FormField label="IP Address" htmlFor="yeastarIp">
              <InputText
                id="yeastarIp"
                value={formData.providerConfig.ip}
                onChange={(e) => setProviderConfig('ip', e.target.value)}
                disabled={isViewMode}
                className="w-full"
                placeholder="Enter IP address"
              />
            </FormField>
            <FormField label="Port" htmlFor="yeastarPort">
              <InputText
                id="yeastarPort"
                value={formData.providerConfig.port}
                onChange={(e) => setProviderConfig('port', e.target.value)}
                disabled={isViewMode}
                className="w-full"
                placeholder="Enter port"
              />
            </FormField>
            <FormField label="Username" htmlFor="yeastarUsername">
              <InputText
                id="yeastarUsername"
                value={formData.providerConfig.yeastarUsername}
                onChange={(e) => setProviderConfig('yeastarUsername', e.target.value)}
                disabled={isViewMode}
                className="w-full"
                placeholder="Enter username"
              />
            </FormField>
            <FormField label="Password" htmlFor="yeastarPassword">
              <InputText
                id="yeastarPassword"
                value={formData.providerConfig.yeastarPassword}
                onChange={(e) => setProviderConfig('yeastarPassword', e.target.value)}
                disabled={isViewMode}
                className="w-full"
                placeholder="Enter password"
                type="password"
              />
            </FormField>
          </FormGrid>
        )
      default:
        return <></>
    }
  }

  const tabs: StepItem[] = [
    {
      key: 'basic',
      step: 1,
      label: 'Basic Info',
      sublabel: 'Name & Status',
      content: (
        <div className="rounded-sm border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="border-b border-gray-100 px-6 py-5">
            <div className="flex items-start gap-3.5">
              <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--kc3-accent)]/10 text-[var(--kc3-accent)]">
                <i className="pi pi-comment text-lg" aria-hidden="true" />
              </span>
              <div className="min-w-0">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-[var(--kc3-accent)]">BASIC INFO</span>
                <h2 className="mt-0.5 text-lg font-bold text-gray-900">Gateway Information</h2>
                <p className="mt-1 text-sm text-gray-500">Name, status, and campaign assignment.</p>
              </div>
            </div>
          </div>
          <div className={bodyClass}>
            <FormGrid columns={2}>
              <FormField label="Status" htmlFor="status">
                <div className="flex items-center gap-2">
                  <InputSwitch
                    inputId="status"
                    checked={formData.status}
                    onChange={(e: { value: boolean }) => setFormData({ ...formData, status: e.value })}
                    disabled={isViewMode}
                  />
                  <span className="text-sm text-gray-600">{formData.status ? 'Active' : 'Inactive'}</span>
                </div>
              </FormField>
              <FormField label="Default" htmlFor="isDefault">
                <div className="flex items-center gap-2">
                  <InputSwitch
                    inputId="isDefault"
                    checked={formData.isDefault}
                    onChange={(e: { value: boolean }) => setFormData({ ...formData, isDefault: e.value })}
                    disabled={isViewMode}
                  />
                  <span className="text-sm text-gray-600">{formData.isDefault ? 'Yes' : 'No'}</span>
                </div>
              </FormField>
            </FormGrid>

            <FormDivider />

            <FormField label="Name" htmlFor="name" required error={errors.name}>
              <InputText
                id="name"
                value={formData.name}
                onChange={(e) => {
                  setFormData({ ...formData, name: e.target.value })
                  if (errors.name) setErrors({ ...errors, name: '' })
                }}
                disabled={isViewMode}
                className={`w-full ${errors.name ? 'p-invalid' : ''}`}
                placeholder="Enter gateway name"
              />
            </FormField>
            <FormField label="Description" htmlFor="description">
              <InputText
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                disabled={isViewMode}
                className="w-full"
                placeholder="Enter description"
              />
            </FormField>
            <FormField label="Campaign" htmlFor="campaignId">
              <Dropdown
                inputId="campaignId"
                value={formData.campaignId}
                onChange={(e) => setFormData({ ...formData, campaignId: e.value })}
                options={campaigns.map((c) => ({ label: c.name, value: c.id }))}
                optionLabel="label"
                optionValue="value"
                disabled={isViewMode || campaignsLoading}
                className="w-full"
                placeholder="Select campaign"
                loading={campaignsLoading}
              />
            </FormField>
          </div>
        </div>
      ),
    },
    {
      key: 'provider',
      step: 2,
      label: 'Provider Configuration',
      sublabel: 'Credentials',
      content: (
        <div className="rounded-sm border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="border-b border-gray-100 px-6 py-5">
            <div className="flex items-start gap-3.5">
              <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--kc3-accent)]/10 text-[var(--kc3-accent)]">
                <i className="pi pi-key text-lg" aria-hidden="true" />
              </span>
              <div className="min-w-0">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-[var(--kc3-accent)]">PROVIDER</span>
                <h2 className="mt-0.5 text-lg font-bold text-gray-900">Provider Configuration</h2>
                <p className="mt-1 text-sm text-gray-500">Select the service provider and enter its credentials.</p>
              </div>
            </div>
          </div>
          <div className={bodyClass}>
            <FormField label="Service Provider" htmlFor="provider" required>
              <Dropdown
                inputId="provider"
                value={formData.provider}
                onChange={(e) => setFormData({ ...formData, provider: e.value as SmsGatewayProvider })}
                options={providerOptions}
                optionLabel="label"
                optionValue="value"
                disabled={isViewMode}
                className="w-full"
                placeholder="Select service provider"
              />
            </FormField>

            <FormDivider />

            {renderProviderFields()}
          </div>
        </div>
      ),
    },
  ]

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <FormContainer className="w-full px-6 py-8">
        <div className="rounded-sm border border-gray-200 bg-white shadow-sm p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <span className="inline-flex items-center rounded-full bg-[var(--kc3-accent)]/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-[var(--kc3-accent)]">
                SMS GATEWAYS
              </span>
              <h1 className="mt-2 text-2xl font-bold tracking-tight text-gray-900">{title}</h1>
              <p className="mt-1 text-sm text-gray-500">{description}</p>
            </div>
            <div className="flex shrink-0 gap-3">
              <button
                type="button"
                onClick={handleCancel}
                disabled={loading}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-5 py-2.5 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50 disabled:cursor-not-allowed disabled:bg-gray-100"
              >
                Cancel
              </button>
              {!isViewMode && (
                <button
                  type="submit"
                  form="sms-gateway-form"
                  disabled={loading}
                  className="inline-flex items-center gap-2 rounded-lg bg-[var(--kc3-accent)] px-5 py-2.5 text-sm font-semibold text-white shadow-md transition-all hover:brightness-110 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:shadow-none"
                >
                  {loading ? (
                    <>
                      <i className="pi pi-spinner pi-spin" aria-hidden="true" />
                      Saving…
                    </>
                  ) : isEditMode ? (
                    'Update'
                  ) : (
                    'Create'
                  )}
                </button>
              )}
            </div>
          </div>
        </div>

        <form id="sms-gateway-form" onSubmit={handleSubmit}>
          <StepperSection steps={tabs} activeIndex={activeIndex} onChange={setActiveIndex} />
        </form>

        {!isViewMode && (
          <StickyFooter
            icon="pi pi-comment"
            eyebrow="You are editing"
            title={formData.name || title}
            closeLabel="Close"
            onClose={handleCancel}
            loading={loading}
          />
        )}
      </FormContainer>
    </div>
  )
}
